(() => {
  const gallerySelector = '[data-cupsland-gallery]';

  const initializeGallery = (gallery) => {
    if (!gallery || gallery.dataset.cupslandGalleryInitialized === 'true') return;

    const mainTrack = gallery.querySelector('[data-cupsland-gallery-main-track]');
    const slides = [...gallery.querySelectorAll('[data-cupsland-gallery-main-slide]')];
    const scrollContainer = gallery.querySelector('[data-cupsland-gallery-scroll]');
    const thumbnails = [...gallery.querySelectorAll('[data-cupsland-gallery-thumb]')];
    const previousButton = gallery.querySelector('[data-cupsland-gallery-prev]');
    const nextButton = gallery.querySelector('[data-cupsland-gallery-next]');

    if (!mainTrack || slides.length === 0) return;
    gallery.dataset.cupslandGalleryInitialized = 'true';

    let activeIndex = Math.min(
      Math.max(Number.parseInt(mainTrack.dataset.startIndex || '0', 10) || 0, 0),
      slides.length - 1
    );
    let mainScrollFrame = 0;

    const updateArrows = () => {
      if (!scrollContainer) return;
      const maxScroll = Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth);
      if (previousButton) previousButton.disabled = scrollContainer.scrollLeft <= 1;
      if (nextButton) nextButton.disabled = scrollContainer.scrollLeft >= maxScroll - 1;
    };

    const centerThumbnail = (thumbnail) => {
      if (!scrollContainer || !thumbnail) return;
      const target = thumbnail.offsetLeft - (scrollContainer.clientWidth - thumbnail.offsetWidth) / 2;
      scrollContainer.scrollTo({ left: Math.max(0, target), behavior: 'auto' });
    };

    const updateActiveState = (index, { moveMain = false, centerThumb = true } = {}) => {
      const nextIndex = Math.min(Math.max(index, 0), slides.length - 1);
      activeIndex = nextIndex;

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      thumbnails.forEach((item, thumbIndex) => {
        const itemIndex = Number.parseInt(item.dataset.galleryIndex || String(thumbIndex), 10);
        const active = itemIndex === nextIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (moveMain) {
        mainTrack.scrollTo({ left: nextIndex * mainTrack.clientWidth, behavior: 'auto' });
      }

      if (centerThumb) {
        const activeThumb = thumbnails.find((item, thumbIndex) => {
          const itemIndex = Number.parseInt(item.dataset.galleryIndex || String(thumbIndex), 10);
          return itemIndex === nextIndex;
        });
        centerThumbnail(activeThumb);
      }
    };

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        const index = Number.parseInt(thumbnail.dataset.galleryIndex || '0', 10) || 0;
        updateActiveState(index, { moveMain: true, centerThumb: true });
      });
    });

    const syncActiveFromMainScroll = () => {
      mainScrollFrame = 0;
      const slideWidth = mainTrack.clientWidth || 1;
      const nextIndex = Math.min(Math.max(Math.round(mainTrack.scrollLeft / slideWidth), 0), slides.length - 1);
      if (nextIndex !== activeIndex) updateActiveState(nextIndex, { moveMain: false, centerThumb: true });
    };

    mainTrack.addEventListener('scroll', () => {
      if (mainScrollFrame) return;
      mainScrollFrame = requestAnimationFrame(syncActiveFromMainScroll);
    }, { passive: true });

    mainTrack.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      updateActiveState(activeIndex + direction, { moveMain: true, centerThumb: true });
    });

    const addMouseDrag = (container) => {
      let dragging = false;
      let moved = false;
      let startX = 0;
      let startScroll = 0;

      container.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = container.scrollLeft;
        container.classList.add('is-dragging');
      });

      container.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const distance = event.clientX - startX;
        if (Math.abs(distance) > 4 && !moved) {
          moved = true;
          container.setPointerCapture(event.pointerId);
        }
        if (moved) container.scrollLeft = startScroll - distance;
      });

      const finishDrag = (event) => {
        if (!dragging) return;
        dragging = false;
        container.classList.remove('is-dragging');
        if (container === mainTrack) {
          const slideWidth = mainTrack.clientWidth || 1;
          const nextIndex = Math.min(Math.max(Math.round(mainTrack.scrollLeft / slideWidth), 0), slides.length - 1);
          updateActiveState(nextIndex, { moveMain: true, centerThumb: true });
        }
        if (container.hasPointerCapture?.(event.pointerId)) container.releasePointerCapture(event.pointerId);
        window.setTimeout(() => {
          moved = false;
        }, 0);
      };

      container.addEventListener('pointerup', finishDrag);
      container.addEventListener('pointercancel', finishDrag);
      container.addEventListener('click', (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }, true);
    };

    addMouseDrag(mainTrack);
    if (scrollContainer) addMouseDrag(scrollContainer);

    previousButton?.addEventListener('click', () => {
      scrollContainer.scrollBy({ left: -scrollContainer.clientWidth * .75, behavior: 'smooth' });
    });

    nextButton?.addEventListener('click', () => {
      scrollContainer.scrollBy({ left: scrollContainer.clientWidth * .75, behavior: 'smooth' });
    });

    scrollContainer?.addEventListener('scroll', () => requestAnimationFrame(updateArrows), { passive: true });

    scrollContainer?.addEventListener('wheel', (event) => {
      if (scrollContainer.scrollWidth <= scrollContainer.clientWidth) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      const before = scrollContainer.scrollLeft;
      scrollContainer.scrollLeft += event.deltaY;
      if (scrollContainer.scrollLeft !== before) event.preventDefault();
    }, { passive: false });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(updateArrows);
      if (scrollContainer) resizeObserver.observe(scrollContainer);
      resizeObserver.observe(mainTrack);
    } else {
      window.addEventListener('resize', updateArrows, { passive: true });
    }

    updateActiveState(activeIndex, { moveMain: true, centerThumb: false });
    updateArrows();

    const preloadImages = () => {
      slides.forEach((slide) => {
        const image = slide.querySelector('img');
        if (!image) return;
        const preload = new Image();
        preload.src = image.currentSrc || image.src;
        if (image.srcset) preload.srcset = image.srcset;
        preload.sizes = image.sizes;
      });
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preloadImages, { timeout: 1800 });
    } else {
      window.setTimeout(preloadImages, 600);
    }
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.(gallerySelector)) initializeGallery(scope);
    scope.querySelectorAll?.(gallerySelector).forEach(initializeGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
